/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1705622400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Jan 19 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "gam360",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Prerequisites",
          "depth": 0,
          "id": "prerequisites"
        }, {
          "value": "Connect to Google",
          "depth": 0,
          "id": "connect-to-google"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "List of identifiers",
          "depth": 1,
          "id": "list-of-identifiers"
        }, {
          "value": "Audience segment identifiers",
          "depth": 1,
          "id": "audience-segment-identifiers"
        }, {
          "value": "Enter filename",
          "depth": 1,
          "id": "enter-filename"
        }, {
          "value": "Set filename offset",
          "depth": 1,
          "id": "set-filename-offset"
        }, {
          "value": "Empty file results",
          "depth": 1,
          "id": "empty-file-results"
        }, {
          "value": "Example exported file",
          "depth": 0,
          "id": "example-exported-file"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      strong: "strong",
      ol: "ol",
      code: "code",
      img: "img",
      h3: "h3",
      em: "em",
      pre: "pre"
    }, _provideComponents(), props.components), {PremiumDestination, NoCommonErrors, SyncAlerts} = _components;
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Google Ad Manager 360"
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Object Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from a source to an Ad Manager cloud storage bucket as a CSV file"
            }), _jsx(_components.td, {
              children: "Insert, All"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["The Google Ad Manager 360 (GAM 360) destination allows you to batch upload identifiers to audience segments in bulk by sending your desired segments to an Ad Manager cloud storage bucket. The segments appear as ", _jsx(_components.a, {
          href: "#example-exported-file",
          children: "a CSV file"
        }), ", which you can then use in GAM 360."]
      }), "\n", _jsx(_components.p, {
        children: "The integration supports these sync modes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": Insert mode creates a CSV file with the rows that were added since the last sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All"
          }), ": All mode creates a CSV file with all the rows in the query results, every time the sync runs."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.p, {
        children: ["Before you can use Hightouch to batch upload your identifiers to GAM 360, ensure you've completed these steps, taken from ", _jsx(_components.a, {
          href: "https://support.google.com/admanager/answer/4349785/",
          children: "Google Ad Manager Help"
        }), ":"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Locate and submit your network code. Find your network code in Ad Manager under ", _jsx(_components.strong, {
            children: "Admin > Global settings > Network code"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Ensure that you've created an active first-party audience segment with which you want to associate the identifiers. If you don't want this segment to collect users using your own inventory, select \"Publisher Managed\" as the Population method."
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Expect to wait up for 24 hours from the time of segment creation to the time when you can perform batch upload."
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://support.google.com/groups/answer/2464926",
            children: "Create a Google Group"
          }), " that contains all Google Accounts that will have access to upload and view files."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Once your account manager has completed your setup, you can create the file of identifiers you want to upload."
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-google",
        children: "Connect to Google"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Google Ad Manager 360"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Google Ad Manager 360 by entering your:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "GCP Credentials"
          }), ": if you need to create a cloud credential, consult ", _jsx(_components.a, {
            href: "/security/gcp",
            children: "our documentation"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "GCP Project ID"
          }), ": you can follow ", _jsx(_components.a, {
            href: "https://support.google.com/googleapi/answer/7014113?hl=en",
            children: "these steps"
          }), " to find it"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "GCS Bucket Name"
          }), ": this be should just be the name of the bucket, not a URL"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've done this, ensure you run the ", _jsx(_components.code, {
          children: "gcloud"
        }), " command to grant Hightouch's service account the ", _jsx(_components.a, {
          href: "https://cloud.google.com/storage/docs/access-control/iam-roles#standard-roles",
          children: "Storage Object Admin"
        }), " role. To run this command, you need to install the ", _jsx(_components.a, {
          href: "https://cloud.google.com/sdk/docs/install",
          children: "gcloud CLI tool"
        }), ". Otherwise, if you use ", _jsx(_components.a, {
          href: "https://groups.google.com/",
          children: "Google Groups"
        }), ", you can also ", _jsx(_components.a, {
          href: "https://support.google.com/a/answer/167094",
          children: "assign this role to a group member"
        }), " by using the Admin console or Google Groups."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gam360-connect.png",
          alt: "Google Ad Manager 360 setup"
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Google Ad Manager 360 destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Google Ad Manager 360 destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "list-of-identifiers",
        children: "List of identifiers"
      }), "\n", _jsx(_components.p, {
        children: "The GAM 360 destination supports several different identifier types."
      }), "\n", _jsx(_components.p, {
        children: "In the first mapping section, select the column corresponding to the list of identifiers you want to batch upload on the left. On the right, select the type of identifiers that are being uploaded to each segment."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gam360-identifiers.png",
          alt: "Record matching in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can find the full list of identifiers in ", _jsx(_components.a, {
          href: "https://support.google.com/admanager/answer/4349785?hl=en&sjid=15048174647173915590-NA",
          children: "Google's docs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gam360-identifier-types.png",
          alt: "Sync configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "audience-segment-identifiers",
        children: "Audience segment identifiers"
      }), "\n", _jsx(_components.p, {
        children: "In the next section, you need to denote the audience segment IDs with which the identifiers from the previous section should be associated. You can either specify a column from your model to associate each identifier with a different segment, or a static value to associate all identifiers with the same segment. Use the toggle on the right of the field to flip between these two options."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gam360-audience.png",
          alt: "Sync configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "enter-filename",
        children: "Enter filename"
      }), "\n", _jsxs(_components.p, {
        children: ["The filename field lets you specify the parent directory and the name of the file you want to use for your results.\nYou can include timestamp variables in the filename, surrounding each with ", _jsx(_components.code, {
          children: "{}"
        }), ".\nHightouch supports these timestamp variables:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YYYY"
          }), ": Represents the full year in four digits."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YY"
          }), ": The last two digits of the year."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "MM"
          }), ": Two-digit month format (01-12)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "DD"
          }), ": Two-digit day format (01-31)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "HH"
          }), ": Two-digit hour format in 24-hour clock (00-23)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "mm"
          }), ": Two-digit minute format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ss"
          }), ": Two-digit second format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ms"
          }), ": Three-digit millisecond format."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "X"
          }), ": Unix timestamp in seconds."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "x"
          }), ": Unix timestamp in milliseconds."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "All dates and times are UTC."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you could enter ", _jsx(_components.code, {
          children: "upload/{YYYY}-{MM}-{DD}-{HH}-{mm}-result.json"
        }), " to dynamically include the year, month, date, hour, and minute in each uploaded file. Hightouch would insert each file in the ", _jsx(_components.code, {
          children: "upload"
        }), " directory, which would need to already exist in your bucket."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use other ", _jsx(_components.a, {
          href: "/syncs/mapping-data#variable-values",
          children: "variable values"
        }), " to include sync metadata in the filename:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.name}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.run.id}"
          })
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If a file already exists at the path you entered at the time of a sync, Hightouch overwrites it."
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-filename-offset",
        children: "Set filename offset"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch uses the timestamp of the sync run to fill in timestamp variables. You can optionally include an offset in seconds. For example, if you want the filename's date to be 24 hours ", _jsx(_components.em, {
          children: "before"
        }), " the sync takes place, enter '-86400' (", _jsx(_components.code, {
          children: "24 hours * 60 minutes * 60 seconds"
        }), ")."]
      }), "\n", _jsx(_components.h3, {
        id: "empty-file-results",
        children: "Empty file results"
      }), "\n", _jsx(_components.p, {
        children: "In insert mode, you can select how Hightouch should handle empty results files. Empty result files can occur if your model's query results haven't changed since the last sync."
      }), "\n", _jsxs(_components.p, {
        children: ["You can select whether to ", _jsx(_components.strong, {
          children: "skip empty files"
        }), ". If you skip empty files, it means Hightouch won't export any files if your model's query results haven't changed since your last sync."]
      }), "\n", _jsx(_components.h2, {
        id: "example-exported-file",
        children: "Example exported file"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Example file content for encrypted cookie IDs:"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "cookie_encrypted,list_id\nScpJKu-yV8je93qkd32MOA,3153490\nw2gsrUcwxF-OiJTRmQswQA,3153490\n"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Example file content for raw AdIDs:"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "cookie_adid,list_id\n38400000-8cf0-11bd-b23e-10b96e40000d,3153490\n38400001-8cf1-11be-b23f-10b96e40000e,3153490\n"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
